/**
 * UserAgentConfig Component
 *
 * Configuration component for browser user agent and viewport settings.
 * Allows users to select from predefined device profiles.
 * (Web version - no browser detection, shows presets + saved task value only)
 *
 * @param {Object} props
 * @param {Object} props.config - Current configuration values
 * @param {Function} props.onChange - Called with new config on any change
 */

import React from "react";
import { HiDevicePhoneMobile } from "react-icons/hi2";
import { FaApple, FaWindows } from "react-icons/fa";
import { TbWorld } from "react-icons/tb";
import { DEVICE_PRESETS, DEVICE_PRESET_ORDER } from "../../utils/devicePresets";

const USER_AGENT_PREFS_KEY = "cloudUserAgentPrefs";

// Icon component based on device type
const DeviceIcon = ({ icon, className }) => {
  switch (icon) {
    case "apple":
      return <FaApple className={className} />;
    case "windows":
      return <FaWindows className={className} />;
    case "browser":
    default:
      return <TbWorld className={className} />;
  }
};

const UserAgentConfig = ({ config, onChange }) => {
  const detectedBrowser = config?.detectedBrowser;

  // Save user preferences to localStorage
  const saveUserPrefs = (newConfig) => {
    try {
      localStorage.setItem(USER_AGENT_PREFS_KEY, JSON.stringify(newConfig));
    } catch (err) {
      console.error("Failed to save user agent preferences:", err);
    }
  };

  const handlePresetSelect = (presetId) => {
    const newConfig = { preset: presetId };
    // Preserve detectedBrowser when selecting "current" so it's not lost on save
    if (presetId === "current" && detectedBrowser) {
      newConfig.detectedBrowser = detectedBrowser;
    }
    onChange(newConfig);
    saveUserPrefs(newConfig);
  };

  // Get viewport dimensions for a preset (use detectedBrowser for "current")
  const getViewportText = (preset) => {
    if (preset.id === "current" && detectedBrowser?.viewport) {
      return `${detectedBrowser.viewport.width}x${detectedBrowser.viewport.height}`;
    }
    return preset.viewport ? `${preset.viewport.width}x${preset.viewport.height}` : "";
  };

  // Get full user agent string for a preset (use detectedBrowser for "current")
  const getUserAgentString = (preset) => {
    if (preset.id === "current" && detectedBrowser?.userAgent) {
      return detectedBrowser.userAgent;
    }
    return preset.userAgent || "";
  };

  // Truncate user agent for display
  const truncateUA = (ua, maxLength = 60) => {
    if (!ua || ua.length <= maxLength) return ua;
    return ua.substring(0, maxLength) + "...";
  };

  // Check if current preset is selected (handle null config)
  const isCurrentPreset = (presetId) => {
    if (!config || config.preset === undefined) return presetId === "chrome_macos";
    return config.preset === presetId;
  };

  // Show "current" preset only when detectedBrowser data exists (task created from extension)
  const availablePresets = DEVICE_PRESET_ORDER.filter(
    (id) => id !== "current" || detectedBrowser
  );

  return (
    <div className="space-y-2.5">
      {/* User Agent Settings Section */}
      <div className="bg-white dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700/30 rounded-2xl p-4 shadow-sm">
        <div className="flex items-center gap-2.5 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700/30">
          <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-500/15">
            <HiDevicePhoneMobile className="text-blue-500 dark:text-blue-400 w-4 h-4" />
          </div>
          <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wide">User Agent</h3>
        </div>

        <div className="space-y-3">
          {/* Device Presets */}
          <div className="py-1.5 px-2 rounded-lg">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Device Profile</label>
            <div className="flex flex-col gap-2">
              {availablePresets.map((presetId) => {
                const preset = DEVICE_PRESETS[presetId];
                const isSelected = isCurrentPreset(presetId);

                return (
                  <button
                    key={presetId}
                    onClick={() => handlePresetSelect(presetId)}
                    className={`relative w-full text-left p-3 pl-10 rounded-xl transition-all duration-200 ${
                      isSelected
                        ? "bg-blue-50 dark:bg-blue-500/15 border border-blue-400 dark:border-blue-500/50 shadow-sm"
                        : "bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 hover:border-gray-300 dark:hover:border-gray-600/60 hover:bg-gray-100 dark:hover:bg-gray-800/60"
                    }`}
                  >
                    {/* Radio indicator */}
                    <div
                      className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                        isSelected ? "border-blue-500 dark:border-blue-400 bg-blue-100 dark:bg-blue-500/20" : "border-gray-400 dark:border-gray-500 bg-transparent"
                      }`}
                    >
                      {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400" />}
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Device icon */}
                      <div
                        className={`p-1.5 rounded-lg ${isSelected ? "bg-blue-100 dark:bg-blue-500/20" : "bg-gray-100 dark:bg-gray-700/50"}`}
                      >
                        <DeviceIcon
                          icon={preset.icon}
                          className={`w-4 h-4 ${isSelected ? "text-blue-500 dark:text-blue-400" : "text-gray-500 dark:text-gray-400"}`}
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm font-medium ${isSelected ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-300"}`}>
                            {preset.id === "current" && detectedBrowser
                              ? `Your ${detectedBrowser.name || "Browser"} on ${detectedBrowser.os || "Unknown"}`
                              : preset.label}
                          </span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className={`text-2xs ${isSelected ? "text-gray-600 dark:text-gray-300" : "text-gray-500"}`}>
                              {getViewportText(preset)}
                            </span>
                            {preset.isMobile && (
                              <span className="text-2xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-500/20 text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30">
                                Mobile
                              </span>
                            )}
                          </div>
                        </div>
                        <p
                          className={`text-2xs mt-1 truncate ${isSelected ? "text-gray-500 dark:text-gray-400" : "text-gray-400 dark:text-gray-600"}`}
                          title={getUserAgentString(preset)}
                        >
                          {truncateUA(getUserAgentString(preset))}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAgentConfig;
